<script setup lang="ts">
import { computed, ref, watch } from 'vue'

export type EnterpriseIconName =
  // Devices & Hardware
  | 'airdrop' | 'airpod' | 'airpods' | 'bluetooth-2' | 'bluetooth-circle'
  | 'bluetooth-rectangle' | 'bluetooth' | 'clock' | 'cpu-charge' | 'cpu-setting'
  | 'cpu' | 'devices' | 'driver-2' | 'driver-refresh' | 'driver' | 'electricity'
  | 'external-drive' | 'game' | 'gameboy' | 'headphone' | 'headphones'
  | 'keyboard-open' | 'keyboard' | 'lamp' | 'microscope' | 'mirroring-screen'
  | 'mobile' | 'monitor-mobbile' | 'monitor-recorder' | 'monitor' | 'mouse'
  | 'music-play' | 'printer-slash' | 'printer' | 'ram-2' | 'ram' | 'simcard-1'
  | 'simcard-2' | 'simcard' | 'speaker' | 'watch-status' | 'watch' | 'weight'

  // Cloud & Storage
  | 'cloud-add' | 'cloud-change' | 'cloud-connection' | 'cloud-remove'
  | 'folder-connection' | 'folder-2' | 'folder-add'

  // Support & Feedback
  | '24-support' | 'dislike' | 'heart-add' | 'heart-circle' | 'heart-edit'
  | 'heart-remove' | 'heart-search' | 'heart-slash' | 'heart-tick' | 'heart'
  | 'like-1' | 'like-dislike' | 'like-shapes' | 'like-tag' | 'like' | 'lovely'
  | 'magic-star' | 'medal-star' | 'medal' | 'message-question' | 'ranking'
  | 'smileys' | 'star-1' | 'star-slash' | 'star' | 'unlimited'

  // Development & Programming
  | 'code-1' | 'code-circle' | 'code' | 'command-square' | 'command' | 'data-2'
  | 'data' | 'document-code-2' | 'document-code' | 'hashtag-down' | 'hashtag-up'
  | 'hashtag' | 'hierarchy-2' | 'hierarchy-3' | 'hierarchy-square-2'
  | 'hierarchy-square-3' | 'hierarchy-square' | 'hierarchy' | 'message-programming'
  | 'mobile-programming' | 'programming-arrow' | 'programming-arrows' | 'scroll'
  | 'sidebar-bottom' | 'sidebar-left' | 'sidebar-right' | 'element-2' | 'element-3'
  | 'element-4' | 'element-equal' | 'element-plus' | 'setting-2' | 'setting-3'
  | 'gallery-add' | 'search-normal' | 'timer' | 'gallery' | 'export' | 'export-1' | 'export-2'
  | 'book' | 'briefcase-tick' | 'shop' | 'cards' | 'presention-chart' | 'brush-1' | 'arrow-right-3' | 'arrow-right' | 'arrow-right-2'
  | 'trash' | 'regenerate' | 'rotate-left' | 'document-copy' | 'clipboard-import'
  | 'edit' | 'more-2' | 'more' | 'import' | 'import-1' | 'gallery-edit'
  | 'video-play' | 'text-block' | 'document-text' | 'arrow-down' | 'user' | 'magicpen' | 'setting'
  | 'security-safe' | 'login' | 'direct-inbox' | 'link' | 'copy' | 'scan-barcode' | 'ruler-pen' | 'candle-2' | 'arrow-left'
  | 'paintbucket' | 'setting-4' | 'colorfilter' | 'info-circle' | 'brush' | 'edit-2' | 'user-2' | 'brush-4'
  | 'coin' | 'tick-circle' | 'notification-bing' | 'speedometer' | 'money-2'

  // Emails, Messages
  | 'messages-3'

  // Video, Audio, Image
  | 'microphone-2'

  // Other
  | 'voice'
  | 'sitemap'
  | 'document-pdf'
  | 'start-lesson'
  | 'note-add' | 'menu' | 'download' | 'shuffle'
  // Classroom custom icons
  | 'exam' | 'refresh' | 'cast_edu' | 'checklist' | 'screen-enter' | 'send' | 'logo'
  | 'flame-filled' | 'flame' | 'edit-square' | 'cast_for_education' | 'english' | 'vietnamese'
  | 'store-03'
  | 'sort'
  | 'book-search' | 'bookmark' | 'heart-filled' | 'heart-filled-like'
  | 'receipt-search'
  | 'layout-sidebar' | 'hand-wave' | 'notebook' | 'target-arrow' | 'lightning-filled' | 'lightning' | 'database'
export type ThemeMode = 'light' | 'dark' | 'auto'
export type IconSize = number | string

interface Props {
  /** Icon name from enterprise folder */
  name: EnterpriseIconName
  /** Icon size (px or string with unit) */
  size?: IconSize
  /** Icon color (CSS color value) */
  color?: string
  /** Theme mode */
  theme?: ThemeMode
  /** Custom CSS class */
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 24,
  color: '',
  theme: 'auto',
  class: '',
})

// Normalize size to string with px
const normalizedSize = computed(() => {
  if (typeof props.size === 'number')
    return `${props.size}px`

  return props.size
})

// Create a static mapping of all icons to avoid dynamic import issues
const iconMap: Record<string, () => Promise<any>> = {
  // Devices & Hardware
  'keyboard-open': () => import('@/assets/iconsax/keyboard-open.svg'),

  // Cloud & Storage

  // Support & Feedback
  'brush-4': () => import('@/assets/iconsax/brush-4.svg'),
  'presention-chart': () => import('@/assets/iconsax/presention-chart.svg'),

  // Development & Programming
  'gallery-edit': () => import('@/assets/iconsax/gallery-edit.svg'),
  'gallery': () => import('@/assets/iconsax/gallery.svg'),
  'document-code-2': () => import('@/assets/iconsax/document-code-2.svg'),
  'arrow-down': () => import('@/assets/iconsax/arrow-down.svg'),
  'user': () => import('@/assets/iconsax/user.svg'),
  'magicpen': () => import('@/assets/iconsax/magicpen.svg'),
  'setting': () => import('@/assets/iconsax/setting.svg'),
  'element-4': () => import('@/assets/iconsax/element-4.svg'),
  'security-safe': () => import('@/assets/iconsax/security-safe.svg'),
  'login': () => import('@/assets/iconsax/login.svg'),

  // Emails, Messages
  'messages-3': () => import('@/assets/iconsax/messages-3.svg'),

  // Video, Audio, Image
  'microphone-2': () => import('@/assets/iconsax/microphone-2.svg'),
  'image': () => import('@/assets/iconsax/image.svg'),

  // Other
  'voice': () => import('@/assets/iconsax/voice.svg'),
  'sitemap': () => import('@/assets/iconsax/sitemap.svg'),
  'document-pdf': () => import('@/assets/iconsax/document-pdf.svg'),

  'note-add': () => import('@/assets/iconsax/note-add.svg'),
  'start-lesson': () => import('@/assets/iconsax/start-lesson.svg'),
  'menu': () => import('@/assets/iconsax/menu.svg'),
  'element-plus': () => import('@/assets/iconsax/element-plus.svg'),

  // Classroom custom icons
  'exam': () => import('@/assets/svg/classroom/exam.svg'),
  'refresh': () => import('@/assets/svg/classroom/refresh.svg'),
  'cast_edu': () => import('@/assets/svg/classroom/cast_edu.svg'),
  'checklist': () => import('@/assets/svg/classroom/checklist.svg'),
  'send': () => import('@/assets/svg/classroom/send.svg'),
  'screen-enter': () => import('@/assets/iconsax/screen-enter.svg'),
  'logo': () => import('@/assets/svg/classroom/logo.svg'),
  'flame-filled': () => import('@/assets/iconsax/flame-filled.svg'),
  'flame': () => import('@/assets/iconsax/flame.svg'),
  'edit-square': () => import('@/assets/iconsax/edit-square.svg'),
  'cast_for_education': () => import('@/assets/iconsax/cast_for_education.svg'),
  'english': () => import('@/assets/classroom-v2/english.svg'),
  'vietnamese': () => import('@/assets/classroom-v2/vietnamese.svg'),
  'book': () => import('@/assets/iconsax/book.svg'),
  'document-text': () => import('@/assets/iconsax/document-text.svg'),
  'trash': () => import('@/assets/iconsax/trash.svg'),
  'direct-inbox': () => import('@/assets/iconsax/direct-inbox.svg'),
  'link': () => import('@/assets/iconsax/link.svg'),
  'copy': () => import('@/assets/iconsax/copy.svg'),
  'export': () => import('@/assets/iconsax/export.svg'),
  'scan-barcode': () => import('@/assets/iconsax/scan-barcode.svg'),
  'ruler-pen': () => import('@/assets/iconsax/ruler-pen.svg'),
  'candle-2': () => import('@/assets/iconsax/candle-2.svg'),
  'arrow-left': () => import('@/assets/iconsax/arrow-left.svg'),
  'paintbucket': () => import('@/assets/iconsax/paintbucket.svg'),
  'setting-4': () => import('@/assets/iconsax/setting-4.svg'),
  'setting-2': () => import('@/assets/iconsax/settings-2.svg'),
  'clock': () => import('@/assets/iconsax/clock.svg'),
  'edit': () => import('@/assets/iconsax/edit.svg'),
  'colorfilter': () => import('@/assets/iconsax/colorfilter.svg'),
  'arrow-right': () => import('@/assets/iconsax/arrow-right.svg'),
  'gallery-add': () => import('@/assets/iconsax/gallery-add.svg'),
  'info-circle': () => import('@/assets/iconsax/info-circle.svg'),
  'store-03': () => import('@/assets/iconsax/store-03.svg'),
  'sort': () => import('@/assets/iconsax/sort.svg'),
  'book-search': () => import('@/assets/svg/classroom/streamline-ultimate_book-search.svg'),
  'arrow-right-2': () => import('@/assets/iconsax/arrow-right-2.svg'),
  'download': () => import('@/assets/iconsax/download.svg'),
  'brush': () => import('@/assets/iconsax/brush.svg'),
  'bookmark': () => import('@/assets/iconsax/bookmark.svg'),
  'heart-filled': () => import('@/assets/iconsax/heart-filled.svg'),
  'heart-filled-like': () => import('@/assets/iconsax/heart-filled-like.svg'),
  'more': () => import('@/assets/iconsax/more.svg'),
  'folder-add': () => import('@/assets/iconsax/folder-add.svg'),
  'user-2': () => import('@/assets/iconsax/user-2.svg'),
  'receipt-search': () => import('@/assets/iconsax/receipt-search.svg'),
  'layout-sidebar': () => import('@/assets/iconsax/layout-sidebar.svg'),
  'hand-wave': () => import('@/assets/iconsax/hand-wave.svg'),
  'edit-2': () => import('@/assets/iconsax/edit-2.svg'),
  'element-3': () => import('@/assets/iconsax/element-3.svg'),
  'coin': () => import('@/assets/iconsax/coin.svg'),
  'tick-circle': () => import('@/assets/iconsax/tick-circle.svg'),
  'notification-bing': () => import('@/assets/iconsax/notification-bing.svg'),
  'speedometer': () => import('@/assets/iconsax/speedometer.svg'),
  'money-2': () => import('@/assets/iconsax/money-2.svg'),
  'shop': () => import('@/assets/iconsax/shop.svg'),
  'briefcase-tick': () => import('@/assets/iconsax/briefcase-tick.svg'),
  'notebook': () => import('@/assets/classroom-v2/notebook.svg'),
  'target-arrow': () => import('@/assets/classroom-v2/target-arrow.svg'),
  'lightning-filled': () => import('@/assets/iconsax/lightning-filled.svg'),
  'lightning': () => import('@/assets/iconsax/lightning.svg'),
  'database': () => import('@/assets/classroom-v2/database.svg'),
  'shuffle': () => import('@/assets/iconsax/shuffle.svg'),
}

// Resolve icon URL via dynamic import
const iconUrl = ref<string | null>(null)

const loadIcon = async () => {
  const importer = iconMap[props.name]
  if (!importer) {
    console.warn(`SvgEIcon: Icon "${props.name}" not found`)
    iconUrl.value = null
    return
  }
  try {
    const mod: any = await importer()
    iconUrl.value = mod?.default || mod
  }
  catch (err) {
    console.warn('[SvgEIcon] Failed to load icon:', err)
    iconUrl.value = null
  }
}

watch(() => props.name, loadIcon, { immediate: true })

// Build mask style to allow color overriding with currentColor or provided color
const maskStyle = computed(() => {
  const size = typeof props.size === 'number' ? `${props.size}px` : props.size
  return {
    width: size,
    height: size,
    backgroundColor: props.color || 'currentColor',
    WebkitMaskImage: iconUrl.value ? `url(${iconUrl.value})` : undefined,
    maskImage: iconUrl.value ? `url(${iconUrl.value})` : undefined,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
  } as Record<string, string | undefined>
})

// Theme class based on theme mode
const themeClass = computed(() => {
  if (props.theme === 'dark')
    return 'enterprise-icon--dark'
  else if (props.theme === 'light')
    return 'enterprise-icon--light'

  return 'enterprise-icon--auto'
})

// Icon container style
const iconStyle = computed(() => ({
  width: normalizedSize.value,
  height: normalizedSize.value,
}))

// svgStyle no longer needed (using CSS mask technique)
</script>

<template>
  <div
    class="enterprise-icon inline-flex items-center justify-center p-0.5" :class="[
      themeClass,
    ]"
    :style="iconStyle"
  >
    <div
      v-if="iconUrl"
      :style="maskStyle"
      aria-hidden="true"
    />
    <div
      v-else
      class="enterprise-icon__fallback text-xs text-gray-400 flex items-center justify-center"
      :style="{ width: normalizedSize, height: normalizedSize }"
    >
      ?
    </div>
  </div>
</template>

<style scoped>
.enterprise-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Auto theme - follows system preference */
.enterprise-icon--auto {
  color: inherit;
}

/* Light theme */
.enterprise-icon--light {
  color: #374151; /* gray-700 */
}

/* Dark theme */
.enterprise-icon--dark {
  color: #f9fafb; /* gray-50 */
}

/* SVG styling */
.enterprise-icon :deep(svg) {
  width: 100%;
  height: 100%;
  fill: currentColor;
  transition: all 0.2s ease;
}

/* Hover effects */
.enterprise-icon:hover :deep(svg) {
  transform: scale(1.05);
}

/* Color variants for common use cases */
.enterprise-icon--primary {
  color: #3b82f6; /* blue-500 */
}

.enterprise-icon--secondary {
  color: #6b7280; /* gray-500 */
}

.enterprise-icon--success {
  color: #10b981; /* emerald-500 */
}

.enterprise-icon--warning {
  color: #f59e0b; /* amber-500 */
}

.enterprise-icon--error {
  color: #ef4444; /* red-500 */
}

/* Size variants */
.enterprise-icon--xs {
  width: 16px;
  height: 16px;
}

.enterprise-icon--sm {
  width: 20px;
  height: 20px;
}

.enterprise-icon--md {
  width: 24px;
  height: 24px;
}

.enterprise-icon--lg {
  width: 32px;
  height: 32px;
}

.enterprise-icon--xl {
  width: 48px;
  height: 48px;
}
</style>
